/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */
import { FormManager } from '@visto-tech/forms';
import { QuestionGroups } from 'components/QuestionGroups/QuestionGroups';
import { QUESTION_INPUT_VARIATIONS } from 'dataLayer/Question';
import Button from 'elements/Button';
import { fromDBForm } from 'hooks/fromDBForm';
import { useAsyncEffect } from 'hooks/useAsyncEffect';
import { formDataToAnswersPayload } from 'hooks/useManagedDatabaseForm';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import Form from '../../dataLayer/Form';
import FormSubmission from '../../dataLayer/FormSubmission';

const variations: { [Key in QUESTION_INPUT_VARIATIONS]: string[] } = {
  'getting-started': ['flex', 'flex-col', 'justify-between', 'relative'],
  standard: [],
  'dynamic-form': ['flex', 'flex-wrap', 'justify-between'],
  'dynamic-form-basic': ['flex', 'flex-wrap', 'justify-between'],
  header: [],
  'no-titles': [],
};

export interface HideComponents {
  groupHeadings?: boolean;
  cta?: boolean;
}

export interface DynamicApiFormValidationSchemaOverride {
  schema?: Record<string, any>;
  excludes?: [string, string][];
}

interface DynamicApiLeadsFormProps {
  formCache?: Form | null;
  formName: string;
  leadEmbedId: string;
  variation: QUESTION_INPUT_VARIATIONS;
  afterSubmit?: (result: Partial<FormSubmission>) => any;
  afterOnClick?: () => any;
  afterLoad?: () => any;
  validationSchemaOverride?: DynamicApiFormValidationSchemaOverride;
}

export const DynamicApiLeadsForm: FC<
  DynamicApiLeadsFormProps & JSX.IntrinsicElements['form']
> = observer(
  ({
    formName,
    leadEmbedId,
    variation = QUESTION_INPUT_VARIATIONS.STANDARD,
    className,
    afterSubmit,
    afterOnClick,
    afterLoad,
    validationSchemaOverride,
    formCache,
  }) => {
    formCache = formCache ? new Form(formCache) : null;

    const recaptchaSiteKey =
      process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY ?? null;

    const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState<boolean>(false);
    const [form, setForm] = useState<null | Form>(formCache ?? null);
    const [formManager, setFormManager] = useState<FormManager<any> | null>(
      formCache ? fromDBForm(formCache, validationSchemaOverride) : null
    );

    const is = {
      gettingStarted: variation === QUESTION_INPUT_VARIATIONS.GETTING_STARTED,
    };

    const refresh = async (status: { isMounted: boolean }) => {
      if (formCache) {
        return;
      }

      const formDb = await Form.getLeadsForm(formName);

      if (status.isMounted && formDb) {
        setForm(formDb);
        const newFormManager = fromDBForm(formDb, validationSchemaOverride);
        setFormManager(newFormManager);
      } else {
        setForm(null);
      }
    };

    useAsyncEffect(refresh, [validationSchemaOverride, formCache]);

    useCallback(
      (form?: Form | null) => {
        if (!form) {
          return null;
        }

        const newFormManager = fromDBForm(form, validationSchemaOverride);
        setFormManager(newFormManager);
      },
      [formManager, validationSchemaOverride]
    );

    useEffect(() => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;

      script.addEventListener('load', () => {
        setIsRecaptchaLoaded(true);
      });

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }, []);

    if (!formManager || !form || !isRecaptchaLoaded || !recaptchaSiteKey) {
      return null;
    }

    afterLoad && afterLoad();

    const handleSubmit = async (_: any, data: any) => {
      const targetWindow = window as any;
      const recaptcha = targetWindow.grecaptcha;

      recaptcha.ready(() => {
        recaptcha
          .execute(recaptchaSiteKey, {
            action: 'submit',
          })
          .then(async (token: string) => {
            await sendForm(data, token);
          });
      });
    };

    const sendForm = async (data: any, recaptchaToken: string) => {
      const promise = Form.submitLeads(
        {
          formName,
          answers: Object.entries(data).flatMap(formDataToAnswersPayload(form)),
        },
        leadEmbedId,
        recaptchaToken
      );

      toast.promise(promise, {
        loading: 'Submitting...',
        success: 'Submitted',
        error: 'Sorry, something went wrong. Please try again.',
      });

      const results = await promise;

      afterSubmit && afterSubmit(results as any);
    };

    return (
      <>
        <form
          id={`form-${formName}`}
          onSubmit={formManager.createSubmitHandler(handleSubmit)}
          className={`dynamicApiLeadsForm ${variations[variation].join(' ')} ${
            className ?? ''
          }`}
        >
          <fieldset className={`w-full`}>
            <QuestionGroups
              variation={variation}
              formManager={formManager}
              formName={formName}
              afterOnClick={afterOnClick}
              form={form}
            >
              {form?.render({
                disabled: false,
                hide: {},
                formManager,
                variation,
              })}
            </QuestionGroups>
          </fieldset>
          {!is.gettingStarted && (
            <Button
              className={`mx-auto`}
              isLoading={formManager.isSubmitting}
              value="PUBLISHED"
              type="submit"
              form={`form-${form?.name}`}
              onClick={() => {
                formManager.clearErrors();
                afterOnClick && afterOnClick();
              }}
            >
              Save
            </Button>
          )}
        </form>
      </>
    );
  }
);

DynamicApiLeadsForm.displayName = 'DynamicApiLeadsForm';
